import type { CityConfig } from "../city-template/types";

export const augustaMeConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates — Maine CDC Vital Records", href: "https://www.maine.gov/dhhs/mecdc/public-health-systems/data-research/vital-records/", description: "Official Maine vital records office in Augusta" },
    { label: "Marriage Licenses — Kennebec County Clerk", href: "https://www.kennebeccounty.org/", description: "Kennebec County Clerk — marriage license issuance" },
    { label: "Divorce Records — Kennebec County Clerk", href: "https://www.kennebeccounty.org/", description: "Kennebec County Clerk — divorce decree records" },
    { label: "VitalChek — Order Vital Records Online", href: "https://www.vitalchek.com/", description: "Official partner of Maine CDC for online vital record orders" },
  ],
  city: "Augusta",
  state: "Maine",
  stateAbbr: "ME",
  slug: "augusta-me",
  route: "/augusta-me",
  title: "Emergency Passport Augusta, ME — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Augusta, Maine — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Augusta, Manchester, Hallowell, and Gardiner travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Augusta, ME",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Augusta and Kennebec County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Augusta%20Maine%20state%20capital%20with%20Maine%20State%20House%20dome%2C%20historic%20government%20district%20along%20the%20Kennebec%20River%2C%20beautiful%20tree-lined%20streets%20with%20classic%20New%20England%20architecture%2C%20warm%20afternoon%20golden%20light%2C%20professional%20cityscape%20photography%2C%20no%20visible%20text%20or%20people%2C%20scenic%20river%20and%20capital%20city%20view&width=1800&height=700&seq=augusta-me-hero-001&orientation=landscape",
  heroImageAlt: "Augusta Maine state capital with Maine State House dome and Kennebec River",
  geoPosition: "44.3106;-69.7795",
  geoPlacename: "Augusta, Maine",
  lastModified: "2026-06-15",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Custom House St, Boston, MA 02110",
    distance: "Located in Boston — approximately 175 miles (3 hours) from Augusta, ME",
  },
  nearbyCitiesZips: [
    { name: "Downtown Augusta", zips: "04330" },
    { name: "Manchester", zips: "04351" },
    { name: "Hallowell", zips: "04347" },
    { name: "Gardiner", zips: "04345" },
    { name: "Winthrop", zips: "04364" },
    { name: "Farmingdale", zips: "04344" },
    { name: "Waterville", zips: "04901" },
    { name: "Sidney", zips: "04330" },
    { name: "Vassalboro", zips: "04989" },
    { name: "China", zips: "04358" },
    { name: "Belgrade", zips: "04917" },
    { name: "Litchfield", zips: "04350" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Augusta departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Portland", route: "/portland-me" },
    { name: "Emergency passport help in Bangor", route: "/bangor" },
    { name: "Urgent passport services in Lewiston", route: "/lewiston" },
    { name: "Same-day passport help in Auburn", route: "/auburn-me" },
    { name: "Expedited passport services in Waterville", route: "/waterville" },
    { name: "Passport courier services in Gardiner", route: "/gardiner" },
    { name: "Rush passport help in Hallowell", route: "/hallowell" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Augusta, Maine remotely via secure courier. We do not have a walk-in office in Augusta — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Augusta travelers.",
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeDistance: "Serving all of Kennebec County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Augusta, the capital city of Maine, and the surrounding Kennebec County area. From downtown Augusta near the State House to the riverfront communities of Hallowell and Gardiner — we help Augusta travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Augusta neighborhoods and nearby cities including Manchester, Hallowell, Gardiner, Winthrop, Farmingdale, and Waterville. If you are in Kennebec County or nearby Androscoggin or Somerset Counties, we have you covered.",
  howItWorksIntro: "Most Augusta travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency. Our emergency and expedited passport process is designed for Maine travelers who need fast, reliable results.",
  mistakesIntro: "As the state capital, Augusta travelers are often heading to government and international destinations. Passport delays can be costly. Here are the most common mistakes we see.",
  testimonialsIntro: "Augusta travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Augusta? Here are the most common ones we hear from Maine travelers.",
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  dmvWebsite: "https://www.maine.gov/sos/bmv",
  dmvLabel: "Maine BMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Augusta international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Patricia K.",
      location: "Augusta, ME",
      rating: 5,
      text: "My flight to Frankfurt was in 4 days and my passport was expired. Fast Passport Center got me a new one in under 48 hours. Shipped from Augusta — I was amazed at the speed.",
      service: "Emergency Passport",
    },
    {
      name: "Robert S.",
      location: "Hallowell, ME",
      rating: 5,
      text: "Needed to renew my passport for a conference in Ottawa. They reviewed my documents thoroughly and I had my passport in 5 days. Excellent service for the capital region.",
      service: "Passport Renewal",
    },
    {
      name: "Jennifer W.",
      location: "Gardiner, ME",
      rating: 5,
      text: "Lost my passport two weeks before my daughter's destination wedding in Jamaica. Fast Passport Center handled the DS-64 and got me a replacement in 2 days. I made the wedding.",
      service: "Lost Passport",
    },
    {
      name: "Daniel F.",
      location: "Winthrop, ME",
      rating: 5,
      text: "I work in state government and travel internationally frequently. Fast Passport Center has been my trusted partner for all my passport needs. Fast, reliable, and always professional.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "augusta-1",
      name: "Augusta Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "40 Western Ave",
      city: "Augusta",
      state: "ME",
      zip: "04330",
      phone: "(207) 622-3139",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Augusta",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "augusta-2",
      name: "Hallowell Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "140 Water St",
      city: "Hallowell",
      state: "ME",
      zip: "04347",
      phone: "(207) 622-6601",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2 miles from downtown Augusta",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Hallowell residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "augusta-3",
      name: "Gardiner Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 Water St",
      city: "Gardiner",
      state: "ME",
      zip: "04345",
      phone: "(207) 582-3700",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8 miles from downtown Augusta",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Good option for Gardiner and Pittston residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "augusta-4",
      name: "Winthrop Town Office",
      type: "Town Office Passport Acceptance",
      address: "17 Highland Ave",
      city: "Winthrop",
      state: "ME",
      zip: "04364",
      phone: "(207) 395-4909",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "6 miles from downtown Augusta",
      website: "https://www.winthropmaine.org/",
      websiteLabel: "Winthrop Town Office",
      notes: "Accepts walk-ins but appointments preferred. Photo services not available on-site.",
      icon: "ri-government-line",
    },
    {
      id: "augusta-5",
      name: "Manchester Town Office",
      type: "Town Office Passport Acceptance",
      address: "202 Readfield Rd",
      city: "Manchester",
      state: "ME",
      zip: "04351",
      phone: "(207) 622-3201",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "5 miles from downtown Augusta",
      website: "https://www.manchester.govoffice.com/",
      websiteLabel: "Manchester Town Office",
      notes: "Small town office with limited hours. Call ahead to schedule passport acceptance.",
      icon: "ri-government-line",
    },
    {
      id: "augusta-6",
      name: "Waterville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1 Elm Plaza",
      city: "Waterville",
      state: "ME",
      zip: "04901",
      phone: "(207) 872-5601",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "18 miles from downtown Augusta",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Waterville and Fairfield residents. Appointment required.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Maine photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Augusta acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Augusta applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Augusta families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Augusta travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Augusta." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Augusta address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Augusta, Maine?",
      a: "Fast Passport Center offers emergency passport processing in Augusta in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Augusta travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Augusta, Maine?",
      a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Augusta residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Augusta, Maine?",
      a: "Fast Passport Center offers fast passport renewal in Augusta with no office visit required. We serve all Augusta neighborhoods and the surrounding Kennebec County area. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Augusta, Maine?",
      a: "The fastest way to get an urgent passport in Augusta is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (3–5 weeks).",
    },
    {
      q: "Do I need to visit the Boston Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Augusta?",
      a: "Popular options include the Augusta Main Post Office (04330), Hallowell Post Office (04347), Gardiner Post Office (04345), and Winthrop Town Office (04364). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in Hallowell, Gardiner, or Winthrop?",
      a: "We serve all of Kennebec County including Hallowell, Gardiner, Winthrop, Manchester, Farmingdale, and Waterville. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Augusta?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Augusta?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and UPS Store locations in Augusta and Hallowell offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Augusta?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Augusta?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Augusta families through the entire process and help schedule acceptance clerk appointments.",
    },
    {
      q: "How far is Augusta from the Boston Passport Agency?",
      a: "Augusta is approximately 175 miles from the Boston Passport Agency — about a 3-hour drive. With Fast Passport Center, you avoid that long trip entirely. We courier your documents directly to State Department facilities, saving you time and the stress of driving to Boston.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Augusta", address: "10 Whitten Rd, Augusta, ME 04330", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walgreens — Augusta", address: "11 Whitten Rd, Augusta, ME 04330", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/" },
    { name: "UPS Store — Augusta", address: "40 Western Ave, Augusta, ME 04330", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/" },
    { name: "Walmart — Augusta", address: "14 Whitten Rd, Augusta, ME 04330", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/" },
    { name: "Rite Aid — Gardiner", address: "20 Water St, Gardiner, ME 04345", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/" },
    { name: "Walgreens — Waterville", address: "10 Concourse, Waterville, ME 04901", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/" },
    { name: "CVS Pharmacy — Hallowell", address: "140 Water St, Hallowell, ME 04347", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walgreens — Winthrop", address: "17 Highland Ave, Winthrop, ME 04364", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/" },
  ],
  dmvLocations: [
    {
      name: "BMV — Augusta",
      address: "19 Anthony Ave, Augusta, ME 04330",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "BMV — Hallowell",
      address: "140 Water St, Hallowell, ME 04347",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "BMV — Gardiner",
      address: "20 Water St, Gardiner, ME 04345",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "BMV — Waterville",
      address: "1 Elm Plaza, Waterville, ME 04901",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center. Online appointment system available.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "55 miles from Augusta (1 hour)",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's busiest airport with direct flights to Canada, Florida, and major U.S. hubs. The primary international departure point for Augusta-area travelers.",
      flightUrl: "https://www.portlandjetport.org/",
      flightLabel: "PWM Flight Info",
      featured: true,
    },
    {
      code: "BGR",
      name: "Bangor International Airport",
      distance: "60 miles from Augusta (1 hour)",
      address: "2870 Godfrey Blvd, Bangor, ME 04401",
      note: "Maine's second busiest airport with seasonal international flights. A closer alternative for some central Maine travelers.",
      flightUrl: "https://www.flybangor.com/",
      flightLabel: "Bangor Airport",
      featured: false,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "175 miles from Augusta (3 hours)",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights to Europe, Asia, and Latin America. Many central Maine travelers connect through Boston for international travel.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan Airport",
      featured: false,
    },
  ],
};